import { useState, useEffect, useRef } from "react";

export default function Footer() {
  const [cookieBannerVisible, setCookieBannerVisible] = useState(false);
  const footerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const accepted = localStorage.getItem("cookies-accepted");
    if (accepted === null) {
      setCookieBannerVisible(true);
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
          }
        });
      },
      { threshold: 0.1 }
    );

    if (footerRef.current) {
      const elements = footerRef.current.querySelectorAll(".animate-on-scroll");
      elements.forEach((el) => observer.observe(el));
    }

    return () => observer.disconnect();
  }, []);

  const handleAcceptCookies = () => {
    localStorage.setItem("cookies-accepted", "true");
    setCookieBannerVisible(false);
  };

  const handleRejectCookies = () => {
    localStorage.setItem("cookies-accepted", "false");
    setCookieBannerVisible(false);
  };

  return (
    <footer ref={footerRef} className="bg-background-100 pt-16 pb-8 border-t border-background-200/70">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-16 animate-on-scroll opacity-0 translate-y-6 transition-all duration-700">
          <div>
            <a href="#accueil" className="h-16 mb-6 block">
              <img
                src="https://static.readdy.ai/image/254cde0d10dfb5832a5e0a0044c661fc/6845293c83c5fe8752674068f47ba81c.png"
                alt="Melio LM"
                className="h-full w-auto"
              />
            </a>
          </div>
          <div>
            <h4 className="text-lg font-semibold mb-6 text-foreground-50">Services</h4>
            <ul className="space-y-3">
              <li>
                <a href="#services" className="text-foreground-50 hover:text-primary-500 transition-colors duration-300">
                  Streat Food Festival
                </a>
              </li>
              <li>
                <a href="#services" className="text-foreground-50 hover:text-primary-500 transition-colors duration-300">
                  Marchés de Noël
                </a>
              </li>
              <li>
                <a href="#services" className="text-foreground-50 hover:text-primary-500 transition-colors duration-300">
                  Événements
                </a>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="text-lg font-semibold mb-6 text-foreground-50">Liens Utiles</h4>
            <ul className="space-y-3">
              <li>
                <a href="#services" className="text-foreground-50 hover:text-primary-500 transition-colors duration-300">
                  Services
                </a>
              </li>
              <li>
                <a href="#portfolio" className="text-foreground-50 hover:text-primary-500 transition-colors duration-300">
                  Portfolio
                </a>
              </li>
              <li>
                <a href="#contact" className="text-foreground-50 hover:text-primary-500 transition-colors duration-300">
                  Contact
                </a>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="text-lg font-semibold mb-6 text-foreground-50">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-start">
                <div className="w-5 h-5 flex items-center justify-center mt-1 mr-3 flex-shrink-0">
                  <i className="ri-phone-line text-primary-500 text-sm"></i>
                </div>
                <a href="tel:+41797612277" className="text-foreground-50 hover:text-primary-500 transition-colors duration-300">
                  +41 79 761 22 77
                </a>
              </li>
              <li className="flex items-start">
                <div className="w-5 h-5 flex items-center justify-center mt-1 mr-3 flex-shrink-0">
                  <i className="ri-map-pin-line text-primary-500 text-sm"></i>
                </div>
                <span className="text-foreground-50">Vevey, Suisse</span>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-background-200/70 pt-8">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-4">
            <a href="/privacy-policy" className="text-foreground-50 hover:text-primary-500 transition-colors duration-300 text-sm">
              Politique de confidentialité
            </a>
            <span className="hidden sm:inline text-foreground-400">|</span>
            <a href="#" className="text-foreground-50 hover:text-primary-500 transition-colors duration-300 text-sm">
              Mentions légales
            </a>
            <span className="hidden sm:inline text-foreground-400">|</span>
            <a href="#" className="text-foreground-50 hover:text-primary-500 transition-colors duration-300 text-sm">
              Gestion des cookies
            </a>
          </div>
          <p className="text-center text-foreground-50">
            &copy; 2025 Melio LM. Tous droits réservés.
          </p>
          <p className="text-center mt-3">
            <a
              href="https://www.manova-digital.ch"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-primary-500 hover:text-primary-400 transition-colors duration-300"
            >
              Conçu et développé par Manova Digital
            </a>
          </p>
        </div>
      </div>

      {/* Cookie Banner */}
      <div
        className={`fixed bottom-0 left-0 w-full bg-background-100 border-t border-background-200/70 p-4 z-50 transition-transform duration-300 ${
          cookieBannerVisible ? "translate-y-0" : "translate-y-full"
        }`}
      >
        <div className="container mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-foreground-400 text-center md:text-left">
            Nous utilisons des cookies pour améliorer votre expérience sur notre site. En continuant à naviguer, vous acceptez notre utilisation des cookies.
          </p>
          <div className="flex gap-4 flex-shrink-0">
            <button
              onClick={handleAcceptCookies}
              className="bg-primary-500 text-background-50 text-sm font-semibold px-6 py-2 rounded-lg whitespace-nowrap hover:bg-primary-600 transition-all duration-300 cursor-pointer"
            >
              Accepter
            </button>
            <button
              onClick={handleRejectCookies}
              className="border border-foreground-400 text-foreground-50 text-sm font-semibold px-6 py-2 rounded-lg whitespace-nowrap hover:bg-background-200 transition-all duration-300 cursor-pointer"
            >
              Refuser
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .animate-in {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
      `}</style>
    </footer>
  );
}